/** Real client for the teamwork routes (api/app/collab/routes.py). Same origin + session cookie as ../http.ts. */
import { ApiError } from '../client'
import { reportUnauthorized } from '../http'
import type { ConversationSummary, SendResult, TagTone } from '../types'
import type {
  AlertSettings,
  AlertValues,
  CollabApi,
  FollowUpInfo,
  FollowUpSendBody,
  Note,
  TagDef,
  TagList,
} from './types'

export class HttpCollabApi implements CollabApi {
  constructor(
    private readonly base: string,
    private readonly timeoutMs = 15000,
    private readonly fetchImpl: typeof fetch = (...a) => fetch(...a),
  ) {}

  private async req<T>(method: string, path: string, body?: unknown, timeoutMs = this.timeoutMs): Promise<T> {
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), timeoutMs)
    const headers: Record<string, string> = { Accept: 'application/json' }
    if (body !== undefined) headers['Content-Type'] = 'application/json'
    const url = new URL(`${this.base.replace(/\/+$/, '')}/api/v1${path}`, window.location.origin).toString()
    try {
      const res = await this.fetchImpl(url, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        credentials: 'same-origin',
        signal: ctrl.signal,
      })
      if (res.status === 401) reportUnauthorized()
      if (!res.ok) {
        let detail = res.statusText
        try {
          const j = (await res.json()) as { detail?: unknown }
          if (typeof j.detail === 'string') detail = j.detail
        } catch {
          /* body not JSON */
        }
        throw new ApiError(res.status, detail || `HTTP ${res.status}`)
      }
      if (res.status === 204) return undefined as T
      return (await res.json()) as T
    } catch (e) {
      if (e instanceof ApiError) throw e
      if (e instanceof DOMException && e.name === 'AbortError') throw new ApiError(0, 'หมดเวลารอเซิร์ฟเวอร์')
      throw new ApiError(0, 'ต่อเซิร์ฟเวอร์ไม่ได้')
    } finally {
      clearTimeout(timer)
    }
  }

  private conv(id: string): string {
    return `/conversations/${encodeURIComponent(id)}`
  }

  async listNotes(convId: string): Promise<Note[]> {
    return (await this.req<{ items: Note[] }>('GET', `${this.conv(convId)}/notes`)).items
  }

  addNote(convId: string, text: string): Promise<Note> {
    return this.req('POST', `${this.conv(convId)}/notes`, { text })
  }

  editNote(noteId: string, text: string): Promise<Note> {
    return this.req('PATCH', `/notes/${encodeURIComponent(noteId)}`, { text })
  }

  deleteNote(noteId: string): Promise<void> {
    return this.req('DELETE', `/notes/${encodeURIComponent(noteId)}`)
  }

  listTags(): Promise<TagList> {
    return this.req('GET', '/tags')
  }

  createTag(name: string, tone: TagTone): Promise<TagDef> {
    return this.req('POST', '/tags', { name, tone })
  }

  updateTag(tagId: string, patch: { name?: string; tone?: TagTone; sort?: number }): Promise<TagDef> {
    return this.req('PATCH', `/tags/${encodeURIComponent(tagId)}`, patch)
  }

  deleteTag(tagId: string): Promise<void> {
    return this.req('DELETE', `/tags/${encodeURIComponent(tagId)}`)
  }

  orderTags(ids: string[]): Promise<TagList> {
    return this.req('PUT', '/tags/order', { ids })
  }

  setConversationTags(convId: string, tagIds: string[]): Promise<{ conversation: ConversationSummary }> {
    return this.req('PUT', `${this.conv(convId)}/tags`, { tag_ids: tagIds })
  }

  /** The AI may write the draft (≤ 8 s on the server): a longer timeout than the default. */
  getFollowUp(convId: string): Promise<FollowUpInfo> {
    return this.req('GET', `${this.conv(convId)}/follow-up`, undefined, 25000)
  }

  sendFollowUp(convId: string, body: FollowUpSendBody): Promise<SendResult> {
    return this.req('POST', `${this.conv(convId)}/follow-up`, body)
  }

  skipFollowUp(convId: string): Promise<{ conversation: ConversationSummary }> {
    return this.req('POST', `${this.conv(convId)}/follow-up/skip`)
  }

  setFollowUpAuto(convId: string, enabled: boolean): Promise<{ enabled: boolean }> {
    return this.req('PUT', `${this.conv(convId)}/follow-up/auto`, { enabled })
  }

  getAlertSettings(): Promise<AlertSettings> {
    return this.req('GET', '/alerts/settings')
  }

  putAlertSettings(changes: Partial<AlertValues>): Promise<AlertSettings> {
    return this.req('PUT', '/alerts/settings', changes)
  }
}
